import { ANALYZE_BILLING_FLOW_FLAG } from './flags.js';
import { resolveFeatureFlag } from './resolve-feature-flag.js';
import { useFeatureFlag } from './use-feature-flag.js';
import { resolveIsSelfServeOrg, useBillingTrack, useIsSelfServeOrg } from './use-is-self-serve-org.js';

import type { AppRouter } from '@cadstrom/backend/trpc';
import type { QueryClient } from '@tanstack/react-query';
import type { TRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { PostHog } from 'posthog-js';

/**
 * Whether to show the self-serve billing/upsell/analyze surface for an org.
 *
 * Gated on the durable self-serve marker ({@link useIsSelfServeOrg}) AND the
 * `analyze-billing-flow` flag. The flag is a global kill switch: rolled out to
 * 100% in normal operation, it matters only when flipped off to instantly hide
 * the surface everywhere. Fails closed (hidden) in every uncertain state.
 *
 * @param orgName - The organization's URL name.
 * @returns Whether the billing surface should be shown for this org.
 */
export function useShowBillingFlow(orgName: string): boolean {
	const isSelfServe = useIsSelfServeOrg(orgName);
	const killSwitchEnabled = useFeatureFlag(ANALYZE_BILLING_FLOW_FLAG) === true;
	return isSelfServe && killSwitchEnabled;
}

/**
 * Whether to show the billing page for an org. Broader than
 * {@link useShowBillingFlow}: a sales-managed org has no self-serve purchase or
 * plan-change path, but still reads its plan, balances and credit packs here, so
 * the page is shown for either track. The `analyze-billing-flow` kill switch
 * still applies, and every uncertain state fails closed (hidden).
 *
 * @param orgName - The organization's URL name.
 * @returns Whether the billing page should be shown for this org.
 */
export function useShowBillingPage(orgName: string): boolean {
	const track = useBillingTrack(orgName);
	const killSwitchEnabled = useFeatureFlag(ANALYZE_BILLING_FLOW_FLAG) === true;
	return track !== 'unprovisioned' && killSwitchEnabled;
}

/**
 * Non-hook form of {@link useShowBillingFlow} for route `beforeLoad`, where hooks
 * are unavailable. Same gate and fail-closed contract. Outside development, an
 * unexpected error from flag resolution is captured and treated as "off" rather
 * than thrown, so a resolver bug cannot escalate a route guard to the global
 * error boundary; development rethrows to keep bugs loud. Errors from the
 * self-serve lookup propagate unchanged.
 *
 * @param queryClient - The router's query client.
 * @param trpc - The tRPC options proxy from the router context.
 * @param posthog - The PostHog client from the router context.
 * @param orgName - The organization's URL name.
 * @returns Whether the billing surface should be shown for this org.
 */
export async function resolveShowBillingFlow(
	queryClient: QueryClient,
	trpc: TRPCOptionsProxy<AppRouter>,
	posthog: PostHog,
	orgName: string,
): Promise<boolean> {
	const [isSelfServe, killSwitchEnabled] = await Promise.all([
		resolveIsSelfServeOrg(queryClient, trpc, orgName),
		resolveFeatureFlag(posthog, ANALYZE_BILLING_FLOW_FLAG).catch((error: unknown) => {
			if (import.meta.env.DEV) {
				throw error;
			}
			posthog.captureException(error, { extra: { source: 'resolve-show-billing-flow', orgName } });
			return false;
		}),
	]);
	return isSelfServe && killSwitchEnabled;
}
